import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import toast from 'react-hot-toast';
import authRequest, { publicRequest } from '../../configs/axios';
import { API_ENDPOINTS } from '../../configs/api.config';

// Load cart from localStorage
const loadCartFromStorage = () => {
  try {
    const savedCart = localStorage.getItem('kacha_bazar_cart');
    return savedCart ? JSON.parse(savedCart) : [];
  } catch (error) {
    console.error('Error loading cart from localStorage:', error);
    return [];
  }
};

// Save cart to localStorage
const saveCartToStorage = (cart) => {
  try {
    localStorage.setItem('kacha_bazar_cart', JSON.stringify(cart));
  } catch (error) {
    console.error('Error saving cart to localStorage:', error);
  }
};

const initialState = {
  productsState: [],
  carouselProducts: [],
  queryProductsState: [],
  categories: [],
  cart: loadCartFromStorage(),
  status: 'idle',
  productsStatus: 'idle',
  carouselStatus: 'idle',
  categoriesStatus: 'idle',
  error: '',
  pagination: {
    totalElements: 0,
    totalPages: 0,
    size: 20,
    number: 0,
  },
};

export const postProductAsync = createAsyncThunk(
  'products/postProductAsync',
  async (payload) => {
    const response = await authRequest.post(API_ENDPOINTS.PRODUCTS, payload);
    return response.data;
  }
);

export const loadCarouselProductsAsync = createAsyncThunk(
  'products/loadCarouselProductsAsync',
  async () => {
    const params = new URLSearchParams();
    params.append('filter', 'inCarrousel:true');
    params.append('sort', 'carrouselOrder,asc');
    params.append('page', 0);
    params.append('size', 50);

    const response = await publicRequest.get(`${API_ENDPOINTS.PRODUCTS}?${params.toString()}`);
    return response.data;
  }
);

export const loadProductsAsync = createAsyncThunk(
  'products/loadProductsAsync',
  async (filter = {}) => {
    const params = new URLSearchParams();
    params.append('page', filter.page || 0);
    params.append('size', filter.size || 100);
    if (filter.status) params.append('status', filter.status);

    const response = await publicRequest.get(`${API_ENDPOINTS.PRODUCTS}?${params.toString()}`);
    return response.data;
  }
);

export const loadQueryProductsAsync = createAsyncThunk(
  'products/loadQueryProductsAsync',
  async ({ categoryIds, page = 0, size = 12 }) => {
    const params = new URLSearchParams();

    // Append each category ID as a separate parameter
    if (categoryIds && categoryIds.length > 0) {
      categoryIds.forEach(id => params.append('categoryIds', id));
    }
    params.append('page', page);
    params.append('size', size);

    const response = await publicRequest.get(`${API_ENDPOINTS.PRODUCTS}?${params.toString()}`);
    return response.data;
  }
);

export const loadSearchProductsAsync = createAsyncThunk(
  'products/loadSearchProductsAsync',
  async ({ searchTerm, page = 0, size = 12 }) => {
    const params = new URLSearchParams();

    // Use Spring Filter syntax for searching by product name
    // Format: filter=name ~ '*searchTerm*' (partial match, case-insensitive)
    params.append('filter', `name ~ '*${searchTerm}*'`);
    params.append('page', page);
    params.append('size', size);

    const response = await publicRequest.get(`${API_ENDPOINTS.PRODUCTS}?${params.toString()}`);
    return response.data;
  }
);

export const loadCategoriesAsync = createAsyncThunk(
  'products/loadCategoriesAsync',
  async () => {
    // Using /list endpoint which returns array directly, not paginated
    const response = await publicRequest.get(API_ENDPOINTS.CATEGORIES);
    return response.data;
  }
);

export const productsSlice = createSlice({
  name: 'products',
  initialState,
  reducers: {
    addToCart: (state, { payload }) => {
      // Check if same product with same packaging option already exists in cart
      const existingItemIndex = state.cart.findIndex((cartItem) => {
        const isSameProduct = (cartItem.item.id || cartItem.item._id) === (payload.item.id || payload.item._id);
        const isSamePackaging = cartItem.packagingOption?.unit === payload.packagingOption?.unit &&
                                cartItem.packagingOption?.price === payload.packagingOption?.price;
        return isSameProduct && isSamePackaging;
      });

      if (existingItemIndex !== -1) {
        // Update quantity and total price if item exists
        state.cart[existingItemIndex].quantity += payload.quantity;
        state.cart[existingItemIndex].totalPrice =
          state.cart[existingItemIndex].quantity * state.cart[existingItemIndex].packagingOption.price;
      } else {
        // Add new item to cart
        state.cart.push(payload);
      }

      // Save to localStorage
      saveCartToStorage(state.cart);
    },
    emptyCart: (state, action) => {
      state.cart = [];
      // Clear localStorage
      saveCartToStorage([]);
    },
    emptyPrev: (state, action) => {
      state.queryProductsState = [];
    },
    handleCancelOrder: (state, { payload }) => {
      // payload should be { productId, packagingUnit, packagingPrice }
      state.cart = state.cart.filter((cartItem) => {
        if (typeof payload === 'string') {
          // Backward compatibility: remove by product ID only
          const itemId = cartItem.item._id || cartItem.item.id;
          return itemId !== payload;
        } else {
          // New way: remove by product ID + packaging option
          const isSameProduct = (cartItem.item.id || cartItem.item._id) === payload.productId;
          const isSamePackaging = cartItem.packagingOption?.unit === payload.packagingUnit &&
                                  cartItem.packagingOption?.price === payload.packagingPrice;
          return !(isSameProduct && isSamePackaging);
        }
      });

      // Save to localStorage
      saveCartToStorage(state.cart);
    },
    handleIncrease: (state, { payload }) => {
      state.cart = state.cart.map((cartItem) => {
        if (typeof payload === 'string') {
          // Backward compatibility: increase by product ID only
          const itemId = cartItem.item._id || cartItem.item.id;
          if (itemId === payload) {
            cartItem.quantity = cartItem.quantity + 1;
            cartItem.totalPrice = Number(cartItem.quantity) * Number(cartItem.packagingOption?.price || cartItem.item.price);
          }
        } else {
          // New way: increase by product ID + packaging option
          const isSameProduct = (cartItem.item.id || cartItem.item._id) === payload.productId;
          const isSamePackaging = cartItem.packagingOption?.unit === payload.packagingUnit &&
                                  cartItem.packagingOption?.price === payload.packagingPrice;
          if (isSameProduct && isSamePackaging) {
            cartItem.quantity = cartItem.quantity + 1;
            cartItem.totalPrice = Number(cartItem.quantity) * Number(cartItem.packagingOption.price);
          }
        }
        return cartItem;
      });

      // Save to localStorage
      saveCartToStorage(state.cart);
    },
    handleDecrease: (state, { payload }) => {
      state.cart = state.cart.map((cartItem) => {
        if (typeof payload === 'string') {
          // Backward compatibility: decrease by product ID only
          const itemId = cartItem.item._id || cartItem.item.id;
          if (itemId === payload && cartItem.quantity > 1) {
            cartItem.quantity = cartItem.quantity - 1;
            cartItem.totalPrice = Number(cartItem.quantity) * Number(cartItem.packagingOption?.price || cartItem.item.price);
          }
        } else {
          // New way: decrease by product ID + packaging option
          const isSameProduct = (cartItem.item.id || cartItem.item._id) === payload.productId;
          const isSamePackaging = cartItem.packagingOption?.unit === payload.packagingUnit &&
                                  cartItem.packagingOption?.price === payload.packagingPrice;
          if (isSameProduct && isSamePackaging && cartItem.quantity > 1) {
            cartItem.quantity = cartItem.quantity - 1;
            cartItem.totalPrice = Number(cartItem.quantity) * Number(cartItem.packagingOption.price);
          }
        }
        return cartItem;
      });

      // Save to localStorage
      saveCartToStorage(state.cart);
    },
    productSorting: (state, { payload }) => {
      const sorter = (pd1, pd2) => {
        // Get price from packagingOptions if available, otherwise use price
        const price1 = pd1.packagingOptions?.[0]?.price || pd1.price || 0;
        const price2 = pd2.packagingOptions?.[0]?.price || pd2.price || 0;

        if (payload === 'low') {
          return +price1 - +price2;
        } else if (payload === 'high') {
          return +price2 - +price1;
        }
      };
      // Sort both states so sorting works for "All" and filtered categories
      state.queryProductsState.sort(sorter);
      state.productsState.sort(sorter);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(postProductAsync.pending, (state, action) => {
      toast.loading('Processing... Please Wait!!');
    });
    builder.addCase(postProductAsync.fulfilled, (state, { payload }) => {
      toast.dismiss();
      toast.success('Your Product is Successfully added!!!');
    });
    builder.addCase(postProductAsync.rejected, (state, { error: { message } }) => {
      toast.dismiss();
      toast.error(message);
    });
    builder.addCase(loadCarouselProductsAsync.pending, (state) => {
      state.carouselStatus = 'Pending';
    });
    builder.addCase(loadCarouselProductsAsync.fulfilled, (state, { payload }) => {
      state.carouselProducts = payload.content || [];
      state.carouselStatus = 'Success';
    });
    builder.addCase(loadCarouselProductsAsync.rejected, (state, { error: { message } }) => {
      state.carouselStatus = 'Rejected';
      state.error = message;
    });
    builder.addCase(loadProductsAsync.pending, (state, action) => {
      state.status = 'Pending';
      state.productsStatus = 'Pending';
    });
    builder.addCase(loadProductsAsync.fulfilled, (state, { payload }) => {
      // Backend returns pageable response with content array
      state.productsState = payload.content || [];
      state.pagination = {
        totalElements: payload.totalElements || 0,
        totalPages: payload.totalPages || 0,
        size: payload.size || 20,
        number: payload.number || 0,
      };
      state.status = 'Success';
      state.productsStatus = 'Success';
    });
    builder.addCase(loadProductsAsync.rejected, (state, { error: { message } }) => {
      state.status = 'Rejected';
      state.productsStatus = 'Rejected';
      state.error = message;
    });
    builder.addCase(loadQueryProductsAsync.pending, (state, action) => {
      state.status = 'Pending';
    });
    builder.addCase(loadQueryProductsAsync.fulfilled, (state, { payload }) => {
      // Backend returns pageable response with content array
      state.queryProductsState = payload.content || [];
      state.pagination = {
        totalElements: payload.totalElements || 0,
        totalPages: payload.totalPages || 0,
        size: payload.size || 20,
        number: payload.number || 0,
      };
      state.status = 'Success';
    });
    builder.addCase(loadQueryProductsAsync.rejected, (state, { error: { message } }) => {
      state.status = 'Rejected';
      state.error = message;
    });
    builder.addCase(loadSearchProductsAsync.pending, (state, action) => {
      state.status = 'Pending';
    });
    builder.addCase(loadSearchProductsAsync.fulfilled, (state, { payload }) => {
      // Backend returns pageable response with content array
      state.queryProductsState = payload.content || [];
      state.pagination = {
        totalElements: payload.totalElements || 0,
        totalPages: payload.totalPages || 0,
        size: payload.size || 20,
        number: payload.number || 0,
      };
      state.status = 'Success';
    });
    builder.addCase(loadSearchProductsAsync.rejected, (state, { error: { message } }) => {
      state.status = 'Rejected';
      state.error = message;
    });
    builder.addCase(loadCategoriesAsync.pending, (state, action) => {
      state.categoriesStatus = 'Pending';
    });
    builder.addCase(loadCategoriesAsync.fulfilled, (state, { payload }) => {
      // Backend returns list array directly (not paginated)
      state.categories = Array.isArray(payload) ? payload : [];
      state.categoriesStatus = 'Success';
    });
    builder.addCase(loadCategoriesAsync.rejected, (state, { error }) => {
      state.categoriesStatus = 'Rejected';
      state.categories = [];
      state.error = error?.message || 'Failed to load categories';
    });
  },
});

export const {
  addToCart,
  emptyCart,
  emptyPrev,
  handleCancelOrder,
  handleIncrease,
  handleDecrease,
  productSorting,
} = productsSlice.actions;

export default productsSlice.reducer;
